<template>
    <van-popup
        v-model="show"
        position="bottom"
        round
        :close-on-click-overlay="!submitting"
        class="pdf-popup"
        @closed="onClosed"
    >
        <div class="pdf-head">
            <span class="pdf-title">{{ step === 'pay' ? '扫码支付' : '选择支付方式' }}</span>
            <span class="pdf-amount">${{ amountText }}</span>
        </div>

        <div class="pdf-body">
            <!-- ============ 第一步：选币种 / 渠道 / 链 ============ -->
            <template v-if="step === 'select'">
                <div v-if="loading" class="pdf-state">
                    <van-loading size="20px">加载中…</van-loading>
                </div>
                <div v-else-if="loadError" class="pdf-state">
                    <p class="pdf-state-txt">支付方式加载失败</p>
                    <span class="pdf-retry" @click="loadChannels">重新加载</span>
                </div>

                <template v-else>
                    <p v-if="errMsg" class="pdf-msg">{{ errMsg }}</p>

                    <!-- 币种 -->
                    <div class="pdf-sec">
                        <h4 class="pdf-sec-t">支付币种</h4>
                        <div class="pdf-currencies">
                            <div
                                v-for="cur in currencies"
                                :key="cur"
                                class="pdf-currency"
                                :class="{ on: cur === currency }"
                                @click="selectCurrency(cur)"
                            >{{ cur }}</div>
                        </div>
                    </div>

                    <!-- 渠道 -->
                    <div class="pdf-sec">
                        <div class="pdf-tabs">
                            <div
                                v-for="tab in tabs"
                                :key="tab.type"
                                class="pdf-tab"
                                :class="{ on: tab.type === tabType }"
                                @click="selectTab(tab.type)"
                            >{{ tab.name }}</div>
                        </div>
                        <div class="pdf-wallets">
                            <div
                                v-for="item in tabChannels"
                                :key="item.code"
                                class="pdf-wallet"
                                :class="{ on: channel && channel.code === item.code, off: !usable(item) }"
                                @click="selectChannel(item)"
                            >
                                <span class="pdf-tick">✓</span>
                                <img v-if="iconOf(item)" class="pdf-icon" :src="iconOf(item)" :alt="item.name">
                                <span v-else class="pdf-letter" :style="{ background: item.color }">{{ letterOf(item) }}</span>
                                <span class="pdf-label">{{ item.name }}</span>
                            </div>
                        </div>
                        <p v-if="hint" class="pdf-hint">{{ hint }}</p>
                    </div>

                    <!-- 网络：仅 Web3 钱包需要 -->
                    <div v-if="networkOptions.length" class="pdf-sec">
                        <h4 class="pdf-sec-t">网络</h4>
                        <div class="pdf-networks">
                            <div
                                v-for="code in networkOptions"
                                :key="code"
                                class="pdf-network"
                                :class="{ on: code === network }"
                                @click="network = code"
                            >{{ networkName(code) }}</div>
                        </div>
                    </div>
                </template>
            </template>

            <!-- ============ 第二步：二维码 + 唤起 + 轮询 ============ -->
            <div v-else class="pdf-sec pdf-pay">
                <img v-if="payData.qrCode" class="pdf-qr" :src="payData.qrCode" alt="支付二维码">
                <p v-if="payData.qrCode" class="pdf-qr-tip">请使用相机（优先）、钱包App扫码支付</p>
                <p class="pdf-pay-meta">{{ paySummary }}</p>
                <div class="pdf-status" :class="payState">
                    <span class="pdf-dot"></span>{{ statusText }}
                </div>
            </div>
        </div>

        <div class="pdf-foot">
            <template v-if="step === 'pay'">
                <van-button
                    v-if="payUrl && payState !== 'paid'"
                    round
                    block
                    color="#111827"
                    @click="launch"
                >{{ launchLabel }}</van-button>
                <div v-if="payState !== 'paid'" class="pdf-sub">
                    <span @click="backToSelect">重新选择</span>
                    <span @click="checkNow">我已完成支付</span>
                </div>
            </template>
            <van-button
                v-else
                round
                block
                color="#111827"
                :disabled="!canSubmit || submitting"
                :loading="submitting"
                loading-text="正在下单…"
                @click="confirm"
            >{{ canSubmit ? '确认支付 $' + amountText : '请选择支付方式' }}</van-button>
        </div>
    </van-popup>
</template>

<script>
/**
 * Paydify 支付弹层（两步）。
 *
 * 步骤 1：选币种 / 交易所或 Web3 钱包 / 链。Paydify 下单时带上 payMethod1 + payMethod2，
 *         网关才会返回可直接唤起钱包 App 的 deeplink；不带则退化为托管收银台(httplink)。
 * 步骤 2：展示网关返回的 qrCode(data:image/png;base64，自带图不用外部请求)，同时给一个
 *         唤起按钮，并轮询订单状态——扫码支付时页面不会跳走，只能靠轮询知道结果。
 *
 * 下单动作留在各业务页(下单参数各页不同)，本组件只负责选择项与支付态。
 *
 * 用法：
 *   <PaydifyWallet ref="paydifyWallet" :amount="price" @confirm="onPaydifyConfirm" @paid="onPaydifyPaid" />
 *   this.$refs.paydifyWallet.open()                      // 打开弹层
 *   onPaydifyConfirm(sel) { ...调 createOrder...
 *       this.$refs.paydifyWallet.deliver(res.data)       // 成功：进入二维码步骤
 *       this.$refs.paydifyWallet.fail('下单失败')         // 失败：恢复按钮
 *   }
 *   onPaydifyPaid() { ...轮询到支付成功... }
 */

// 钱包图标。文件名 = 渠道 code 小写，缺图标时降级为品牌色首字母块，
// 所以这里不必与后端渠道目录逐一对齐
const WALLET_ICONS = (function () {
    const map = {};
    try {
        const ctx = require.context('@/assets/images/wallet', false, /\.png$/);
        ctx.keys().forEach(function (key) {
            const name = key.replace(/^\.\//, '').replace(/\.png$/, '').toLowerCase();
            const mod = ctx(key);
            map[name] = (mod && mod.default) || mod;
        });
    } catch (e) {
        // 目录不存在时全部走字母块兜底
    }
    return map;
})();

// 轮询间隔与上限。Paydify 会话默认 30 分钟过期，这里守到 15 分钟就停，
// 之后由 Webhook 结算 + 用户回订单页查看，不再空转
const POLL_INTERVAL = 3000;
const POLL_MAX = 300;

export default {
    name: 'PaydifyWallet',
    props: {
        // 订单金额，仅用于展示
        amount: {
            type: [Number, String],
            default: 0
        }
    },
    data() {
        return {
            show: false,
            step: 'select',
            loading: false,
            loadError: false,
            submitting: false,
            errMsg: '',

            tabs: [
                { type: 'cex', name: '交易所' },
                { type: 'wallet', name: 'Web3 钱包' }
            ],
            tabType: 'cex',

            currencies: [],
            currencyNetworks: {},
            networkMap: {},
            channels: [],

            currency: '',
            channel: null,
            network: '',

            // 第二步状态
            payData: {},
            payState: 'waiting', // waiting / paid / timeout
            pollTimer: null,
            pollTicks: 0,
            polling: false
        };
    },
    computed: {
        amountText() {
            const n = parseFloat(this.amount);
            return isNaN(n) ? '0.00' : n.toFixed(2);
        },
        tabChannels() {
            const type = this.tabType;
            return this.channels.filter(function (c) { return c.type === type; });
        },
        // 当前渠道在当前币种下可选的链；交易所渠道恒为空
        networkOptions() {
            return this.channel ? this.available(this.channel) : [];
        },
        canSubmit() {
            if (!this.channel) return false;
            return this.channel.type !== 'wallet' || this.network !== '';
        },
        hint() {
            const tips = [];
            const self = this;
            const anyOff = this.tabChannels.some(function (c) { return !self.usable(c); });
            if (anyOff) tips.push('置灰的方式不支持 ' + this.currency);
            // 只有部分渠道能直接唤起 App，其余会跳 Paydify 收银台，提前告知避免落差
            if (this.channel && !this.channel.deeplink) {
                tips.push(this.channel.name + ' 需在 Paydify 收银台完成支付');
            }
            return tips.join('；');
        },
        isMobile() {
            return /Android|iPhone|iPad|iPod|Mobile|HarmonyOS/i.test(navigator.userAgent);
        },
        // 移动端优先 deeplink 唤起 App；桌面端 deeplink 唤不起来，优先收银台页
        payUrl() {
            const d = this.payData || {};
            return this.isMobile
                ? (d.deeplink || d.httplink || '')
                : (d.httplink || d.deeplink || '');
        },
        launchLabel() {
            const d = this.payData || {};
            if (this.isMobile && d.deeplink) return '打开钱包 App 支付';
            return '打开支付页面';
        },
        paySummary() {
            const parts = [];
            if (this.channel) parts.push(this.channel.name);
            if (this.currency) parts.push(this.currency);
            if (this.network) parts.push(this.networkName(this.network));
            return parts.join(' · ');
        },
        statusText() {
            if (this.payState === 'paid') return '支付成功，正在跳转…';
            if (this.payState === 'timeout') return '等待超时，若已付款请稍后在订单中查看';
            return '等待支付中…';
        }
    },
    methods: {
        /** 打开弹层。首次打开时拉渠道目录 */
        open() {
            this.stopPoll();
            this.step = 'select';
            this.errMsg = '';
            this.submitting = false;
            this.payData = {};
            this.payState = 'waiting';
            this.show = true;
            if (!this.channels.length) {
                this.loadChannels();
            }
        },
        close() {
            this.show = false;
        },
        onClosed() {
            // 关掉弹层就别再空转轮询了
            this.submitting = false;
            this.stopPoll();
        },

        loadChannels() {
            this.loading = true;
            this.loadError = false;
            this.$api.shopping.paydifyChannels({}).then((res) => {
                this.loading = false;
                if (!res || res.code != 200 || !res.data) {
                    this.loadError = true;
                    return;
                }
                const d = res.data;
                this.currencies = d.currencies || [];
                this.currencyNetworks = d.currencyNetworks || {};
                this.networkMap = d.networks || {};
                this.channels = d.channels || [];
                this.currency = d.defaultCurrency || this.currencies[0] || '';
            }).catch(() => {
                this.loading = false;
                this.loadError = true;
            });
        },

        /** 某渠道在当前币种下可选的链 = 渠道支持的链 ∩ 该币种支持的链 */
        available(channel) {
            if (!channel || channel.type !== 'wallet') return [];
            const allowed = this.currencyNetworks[this.currency] || [];
            return (channel.networks || []).filter(function (n) { return allowed.indexOf(n) !== -1; });
        },
        /** 该渠道当前是否可选：交易所恒可选，钱包需至少有一条可用链 */
        usable(channel) {
            if (channel.type !== 'wallet') return true;
            return this.available(channel).length > 0;
        },
        iconOf(channel) {
            return WALLET_ICONS[String(channel.code).toLowerCase()] || '';
        },
        letterOf(channel) {
            return String(channel.name || channel.code).charAt(0).toUpperCase();
        },
        networkName(code) {
            return this.networkMap[code] || code;
        },

        selectCurrency(cur) {
            if (this.currency === cur) return;
            this.currency = cur;
            // 换币种后原钱包可能不再支持，清掉选择
            if (this.channel && !this.usable(this.channel)) {
                this.channel = null;
                this.network = '';
                return;
            }
            // 原链在新币种下可能不可用，兜底选回第一条
            const nets = this.available(this.channel);
            if (nets.length && nets.indexOf(this.network) === -1) {
                this.network = nets[0];
            }
        },
        // 两组渠道互斥，切 tab 时清掉已选，避免"选了交易所却停在钱包页"
        selectTab(type) {
            if (this.tabType === type) return;
            this.tabType = type;
            this.channel = null;
            this.network = '';
            this.errMsg = '';
        },
        selectChannel(item) {
            if (!this.usable(item)) return;
            this.channel = item;
            // Web3 钱包默认选中第一条可用链
            const nets = this.available(item);
            this.network = nets.length ? nets[0] : '';
            this.errMsg = '';
        },

        /** 确认支付：把选择项交给业务页去下单 */
        confirm() {
            if (!this.canSubmit || this.submitting) return;
            this.submitting = true;
            this.errMsg = '';
            this.$emit('confirm', {
                payMethod1: this.channel.code,
                payMethod2: this.network,
                payCurrency: this.currency
            });
        },

        /**
         * 下单成功：进入二维码步骤，并开始轮询订单状态。
         *
         * 扫码支付时页面始终留在这里，唤起 App 也只是切走前台，都不会触发页面跳转，
         * 所以支付结果只能靠轮询。不再自动唤起钱包，需用户手动点击按钮跳转。
         *
         * @param {Object} data createOrder 返回的 data(deeplink / httplink / qrCode / order_sn)
         * @return {Boolean} 是否成功进入支付步骤
         */
        deliver(data) {
            const d = data || {};
            if (!d.deeplink && !d.httplink && !d.qrCode) {
                this.fail('下单失败，请稍后重试');
                return false;
            }
            this.payData = d;
            this.payState = 'waiting';
            this.submitting = false;
            this.step = 'pay';
            this.startPoll();
            return true;
        },
        /** 下单失败：恢复按钮并提示 */
        fail(msg) {
            this.submitting = false;
            this.errMsg = msg || '';
        },
        launch() {
            if (this.payUrl) {
                window.location.href = this.payUrl;
            }
        },
        backToSelect() {
            this.stopPoll();
            this.step = 'select';
            this.payData = {};
            this.payState = 'waiting';
        },

        startPoll() {
            this.stopPoll();
            this.pollTicks = 0;
            this.pollTimer = setInterval(this.pollOnce, POLL_INTERVAL);
        },
        stopPoll() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
            this.polling = false;
        },
        /** 用户手动点"我已完成支付"：立刻查一次，不等下一个轮询周期 */
        checkNow() {
            this.pollOnce();
        },
        pollOnce() {
            const orderSn = this.payData && this.payData.order_sn;
            if (!orderSn || this.payState === 'paid') return;
            // 上一次还没回来就跳过这一轮，避免慢网络下请求堆积
            if (this.polling) return;

            this.pollTicks++;
            if (this.pollTicks > POLL_MAX) {
                this.stopPoll();
                this.payState = 'timeout';
                return;
            }

            this.polling = true;
            //未支付时后端返回 code=400「支付未完成」，走 silent 免得每 3 秒弹一次提示
            this.$api.shopping.checkOrderByOrderSn({ order_sn: orderSn }).then((res) => {
                this.polling = false;
                if (res && res.code == 200) {
                    this.onPaid();
                }
            }).catch(() => {
                this.polling = false;
            });
        },
        onPaid() {
            this.stopPoll();
            this.payState = 'paid';
            localStorage.removeItem('isPay');
            this.$emit('paid', this.payData);
            this.show = false;
        }
    },
    beforeDestroy() {
        this.stopPoll();
    }
};
</script>

<style scoped>
.pdf-popup {
    max-height: 85%;
    display: flex;
    flex-direction: column;
    background: #f2f3f5;
}
.pdf-head {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1rem .75rem;
    background: #fff;
}
.pdf-title { font-size: .95rem; font-weight: 600; color: #1f2937; }
.pdf-amount { font-size: 1.4rem; font-weight: 700; color: #ff4e00; }

.pdf-body {
    flex: 1 1 auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: .75rem;
}
.pdf-state { padding: 2.5rem 0; text-align: center; }
.pdf-state-txt { margin: 0 0 .75rem; font-size: .85rem; color: #6b7280; }
.pdf-retry { font-size: .85rem; color: #19A0E5; }

.pdf-msg {
    margin: 0 0 .75rem;
    padding: .7rem .8rem;
    border-radius: .5rem;
    background: #fef2f2;
    color: #b91c1c;
    font-size: .8rem;
    line-height: 1.6;
    text-align: left;
}

.pdf-sec { background: #fff; border-radius: .7rem; padding: .8rem; margin-bottom: .6rem; }
.pdf-sec-t {
    margin: 0 0 .7rem;
    font-size: .8rem;
    font-weight: 600;
    color: #6b7280;
    text-align: left;
}

.pdf-currencies { display: flex; }
.pdf-currency {
    flex: 1;
    height: 2.6rem;
    line-height: 2.5rem;
    margin-right: .6rem;
    border: .08rem solid #e5e7eb;
    border-radius: .5rem;
    font-size: .9rem;
    font-weight: 600;
    color: #374151;
    text-align: center;
}
.pdf-currency:last-child { margin-right: 0; }
.pdf-currency.on { border-color: #16c79a; background: #f0fdf9; color: #0f766e; }

.pdf-tabs {
    display: flex;
    background: #f2f3f5;
    border-radius: .5rem;
    padding: .2rem;
    margin-bottom: .8rem;
}
.pdf-tab {
    flex: 1;
    height: 2rem;
    line-height: 2rem;
    border-radius: .4rem;
    font-size: .8rem;
    font-weight: 600;
    color: #6b7280;
    text-align: center;
}
.pdf-tab.on { background: #fff; color: #111827; box-shadow: 0 .05rem .15rem rgba(0,0,0,.08); }

.pdf-wallets { display: flex; flex-wrap: wrap; }
.pdf-wallet {
    position: relative;
    width: 22%;
    margin: 0 4% 3% 0;
    border: .08rem solid #e5e7eb;
    border-radius: .6rem;
    padding: .5rem .1rem .4rem;
    text-align: center;
}
.pdf-wallet:nth-child(4n) { margin-right: 0; }
.pdf-wallet.on { border-color: #16c79a; background: #f0fdf9; }
.pdf-wallet.off { opacity: .38; }
.pdf-icon,
.pdf-letter {
    width: 1.9rem;
    height: 1.9rem;
    margin: 0 auto .3rem;
    border-radius: .45rem;
    display: block;
    object-fit: contain;
}
.pdf-letter {
    line-height: 1.9rem;
    color: #fff;
    font-size: .85rem;
    font-weight: 700;
}
.pdf-label {
    display: block;
    font-size: .6rem;
    line-height: 1.3;
    color: #374151;
    word-break: break-word;
}
.pdf-tick {
    position: absolute;
    top: -.3rem;
    right: -.3rem;
    width: .95rem;
    height: .95rem;
    line-height: .95rem;
    border-radius: 50%;
    background: #16c79a;
    color: #fff;
    font-size: .55rem;
    display: none;
}
.pdf-wallet.on .pdf-tick { display: block; }

.pdf-hint { margin: .3rem .1rem 0; font-size: .65rem; line-height: 1.6; color: #9099a6; text-align: left; }

.pdf-networks { display: flex; flex-wrap: wrap; }
.pdf-network {
    min-width: 30%;
    height: 2.2rem;
    line-height: 2.1rem;
    padding: 0 .6rem;
    margin: 0 3% 3% 0;
    border: .08rem solid #e5e7eb;
    border-radius: .5rem;
    font-size: .75rem;
    font-weight: 600;
    color: #374151;
    text-align: center;
}
.pdf-network.on { border-color: #16c79a; background: #f0fdf9; color: #0f766e; }

/* 第二步：二维码 */
.pdf-pay { padding: 1.2rem .8rem 1rem; text-align: center; }
.pdf-qr {
    width: 10rem;
    height: 10rem;
    display: block;
    margin: 0 auto .7rem;
    border: .08rem solid #eceef1;
    border-radius: .5rem;
}
.pdf-qr-tip { margin: 0 0 .4rem; font-size: .75rem; color: #6b7280; }
.pdf-pay-meta { margin: 0 0 .9rem; font-size: .7rem; color: #9099a6; }
.pdf-status { font-size: .75rem; color: #6b7280; }
.pdf-status .pdf-dot {
    display: inline-block;
    width: .4rem;
    height: .4rem;
    margin-right: .35rem;
    border-radius: 50%;
    background: #f7a600;
    vertical-align: middle;
}
.pdf-status.paid { color: #0f766e; }
.pdf-status.paid .pdf-dot { background: #16c79a; }
.pdf-status.timeout { color: #b91c1c; }
.pdf-status.timeout .pdf-dot { background: #b91c1c; }

.pdf-foot {
    flex: none;
    padding: .7rem 1rem calc(.7rem + constant(safe-area-inset-bottom));
    padding-bottom: calc(.7rem + env(safe-area-inset-bottom));
    background: #fff;
    border-top: .04rem solid #eceef1;
}
.pdf-sub {
    display: flex;
    justify-content: space-between;
    margin-top: .6rem;
    font-size: .75rem;
    color: #19A0E5;
}
</style>
